/**
 * IMPORTANT: English comments only. DO NOT use Chinese characters in comments.
 * All Chinese comments will be removed during code reviews.
 */
import { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { useSubscription } from '../context/SubscriptionContext';
import { format } from 'date-fns';
import { toast } from 'react-hot-toast';

import NoSubscriptionView from '../components/subscription/NoSubscriptionView';
import SubscriptionHeader from '../components/subscription/SubscriptionHeader';
import SubscriptionMainContent from '../components/subscription/SubscriptionMainContent';
import SubscriptionModals from '../components/subscription/SubscriptionModals';
import AddressManagementModal from '../components/AddressManagementModal';
import PageHeader from '../components/ui/PageHeader';

import { useSubscriptionActions } from '../hooks/useSubscriptionActions';
import { filterSubscriptions } from '../utils/subscriptionUtils';
import { deliveryService } from '../services';
import { AddressWithId } from '../services/addressService';

import { Address } from '../types/api';
import { SubscriptionState, DeliverySchedule } from '../types';

import useBottomNavPadding from '../hooks/useBottomNavPadding';
import { useTranslation } from 'react-i18next';
import { FEATURES } from '../config';
import ShutdownNotice from '../components/MaintenanceNotice';

// Enhanced DeliverySchedule to include id
interface EnhancedDeliverySchedule extends DeliverySchedule {
  id?: string;
  subscriptionId?: string;
}

export default function SubscriptionManagement() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { subscriptionId } = useParams<{ subscriptionId: string }>();
  const { search } = useLocation();
  const queryParams = new URLSearchParams(search);
  const initialTab = queryParams.get('tab') as 'upcoming' | 'past' || 'upcoming';
  
  // Apply bottom padding for the navigation
  useBottomNavPadding();
  
  const { 
    subscriptions,
    loading,
    renewSubscription, 
    upgradeSubscription, 
    updateAddress,
    fetchSubscriptionData
  } = useSubscription();
  
  // State for subscriptions
  const [activeTab, setActiveTab] = useState<'upcoming' | 'past'>(initialTab);
  const [showCancelledSubscriptions, setShowCancelledSubscriptions] = useState(false);
  const [selectedCancelledSubscription, setSelectedCancelledSubscription] = useState<SubscriptionState | null>(null);
  const [activeSubscriptionIndex, setActiveSubscriptionIndex] = useState(0);
  const [upcomingDeliveries, setUpcomingDeliveries] = useState<EnhancedDeliverySchedule[]>([]);
  const [pastDeliveries, setPastDeliveries] = useState<EnhancedDeliverySchedule[]>([]);
  const [processingDeliveryId, setProcessingDeliveryId] = useState<string | null>(null);
  
  // Address modal state
  const [showAddressModal, setShowAddressModal] = useState(false);
  
  // Refs
  const loadedSubscriptionIds = useRef<Set<string>>(new Set());
  const loadingSubscriptionId = useRef<string | null>(null);
  const isSwitchingSubscription = useRef<boolean>(false);
  const userHasSelectedSubscription = useRef<boolean>(false);
  const hasInitialActiveSelectionDone = useRef<boolean>(false);
  const loadedDeliveriesIds = useRef<Set<string>>(new Set());
  const fetchingDeliveriesId = useRef<string | null>(null);
  const initializedRef = useRef<boolean>(false);
  
  const {
    activeSubscription,
    showSkipModal,
    setShowSkipModal,
    skipDate,
    setSkipDate,
    handleSkipDelivery,
    handleSkipDeliveryFromHistory,
    handlePauseSubscription,
    handleResumeSubscription,
    handleCancelSubscription,
    handleAddressUpdate
  } = useSubscriptionActions({
    subscriptions,
    setSubscriptions: () => {}, // No need to set subscriptions as we're using context
    activeSubscriptionIndex,
    upcomingDeliveries,
    setUpcomingDeliveries,
    setProcessingDeliveryId,
    setLoading: () => {}, // No need to set loading as we're using context
    fetchSubscriptionData
  });

  
  useEffect(() => {
    if (!initializedRef.current) {
      console.log('Initializing SubscriptionManagement component state');
      loadedSubscriptionIds.current = new Set();
      loadingSubscriptionId.current = null;
      isSwitchingSubscription.current = false;
      userHasSelectedSubscription.current = false;
      hasInitialActiveSelectionDone.current = false;
      loadedDeliveriesIds.current = new Set();
      fetchingDeliveriesId.current = null;
      initializedRef.current = true;
      
      // Force a refetch of delivery data on page refresh
      if (subscriptions.length > 0 && subscriptions[activeSubscriptionIndex]?.id) {
        console.log('Page refresh detected - forcing delivery data refetch');
        const subscriptionId = subscriptions[activeSubscriptionIndex].id;
        if (subscriptionId) {
          fetchingDeliveriesId.current = null;
          loadedDeliveriesIds.current = new Set();
        }
      }
    }
    
    
    return () => {
      console.log('Cleaning up SubscriptionManagement component state');
      initializedRef.current = false;
      loadedSubscriptionIds.current = new Set();
      loadedDeliveriesIds.current = new Set();
    };
  }, [subscriptions, activeSubscriptionIndex]);

  // Handle subscription ID changes
  useEffect(() => {
    if (subscriptions.length === 0) {
      return; 
    }
    
    if (!subscriptionId) {
      // If no subscription ID is provided, redirect to the subscription list
      navigate('/subscriptions', { replace: true });
      return;
    }
    
    // Check if the subscription ID exists in our current list of subscriptions
    const index = subscriptions.findIndex(sub => sub.id === subscriptionId);
    console.log('Finding subscription by ID:', { 
      subscriptionId, 
      found: index !== -1, 
      index, 
      currentIndex: activeSubscriptionIndex,
      totalSubscriptions: subscriptions.length
    });
    
    // If subscription is not found in our current list
    if (index === -1) {
      console.warn(`Subscription with ID ${subscriptionId} not found in current list, may have been deleted`);
      
      // Remove from our tracking sets to prevent future requests
      loadedSubscriptionIds.current.delete(subscriptionId);
      loadedDeliveriesIds.current.delete(subscriptionId);
      
      // Redirect to subscription list
      toast.error('The selected subscription is no longer available');
      navigate('/subscriptions', { replace: true });
      return;
    }
      
    const subscription = subscriptions[index];
    if (subscription?.status !== 'active' && subscription?.status !== 'paused') {
      setShowCancelledSubscriptions(true);
    }
    
    if (index !== activeSubscriptionIndex) {
      setActiveSubscriptionIndex(index);
    }
    
    setTimeout(() => {
      const selectedCard = document.querySelector(`[data-subscription-index="${index}"]`);
      if (selectedCard) {
        selectedCard.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
      }
    }, 100);
      
    // If we've already loaded this subscription, no need to fetch again
    if (loadedSubscriptionIds.current.has(subscriptionId)) {
      isSwitchingSubscription.current = false;
      loadingSubscriptionId.current = null;
      return;
    }
      
    // Fetch fresh data for this subscription
    if (!loadedSubscriptionIds.current.has(subscriptionId)) {
      isSwitchingSubscription.current = true;
      loadingSubscriptionId.current = subscriptionId;
        
      fetchSubscriptionData(subscriptionId)
        .then(() => {
          loadedSubscriptionIds.current.add(subscriptionId);
          if (loadingSubscriptionId.current === subscriptionId) {
            isSwitchingSubscription.current = false;
            loadingSubscriptionId.current = null;
          }
        })
        .catch((error) => {
          console.error('Error loading subscription data:', error);
          
          // Check if this error is due to a 404 Not Found for the subscription
          if (error.response?.status === 404 && error.response?.data?.error === "Subscription not found") {
            console.warn(`Subscription ${subscriptionId} no longer exists in the database`);
            
            // Remove from our tracking sets
            loadedSubscriptionIds.current.delete(subscriptionId);
            loadedDeliveriesIds.current.delete(subscriptionId);
            
            // Redirect to subscription list
            toast.error('The subscription is no longer available');
            navigate('/subscriptions', { replace: true });
          } else {
            // For other errors show the regular error toast
            toast.error('Failed to load subscription data');
          }
          
          isSwitchingSubscription.current = false;
          loadingSubscriptionId.current = null;
        });
    }
  }, [subscriptionId, subscriptions, navigate]);

  
  useEffect(() => {
    if (subscriptions.length > 0 && loadedSubscriptionIds.current.size === 0) {
      console.log('Initializing loaded subscription IDs');
      
      subscriptions.forEach(sub => {
        if (sub.id) {
          loadedSubscriptionIds.current.add(sub.id);
        }
      });
      
      
      isSwitchingSubscription.current = false;
      loadingSubscriptionId.current = null;
    }
  }, [subscriptions]);

  // Reset loading state when subscriptions are loaded
  useEffect(() => {
    if (subscriptions.length > 0 && !loading) {
      isSwitchingSubscription.current = false;
      loadingSubscriptionId.current = null;
    }
  }, [subscriptions, loading]);

  // Fetch deliveries when subscription changes or on mount
  useEffect(() => {
    if (subscriptions.length === 0 || !subscriptionId || !subscriptions[activeSubscriptionIndex]) {
      return;
    }
    
    const subscription = subscriptions[activeSubscriptionIndex];
    const currentSubscriptionId = subscription.id;
    
    if (!currentSubscriptionId) {
      return;
    }
    
    // On page refresh initializedRef will be false, so we always fetch
    // Only skip if we're already fetching this subscription's deliveries
    if (fetchingDeliveriesId.current === currentSubscriptionId) {
      console.log('Skipping duplicate deliveries fetch - already in progress for:', currentSubscriptionId);
      return;
    }
    
    // Only skip if we've already loaded this subscription's deliveries AND this is not a page refresh
    if (loadedDeliveriesIds.current.has(currentSubscriptionId) && initializedRef.current) {
      console.log('Skipping deliveries fetch - already loaded for:', currentSubscriptionId);
      return;
    }
    
    // Check if the subscription is valid for fetching deliveries
    // Skip fetching for cancelled/deleted subscriptions
    if (subscription.status !== 'active' && subscription.status !== 'paused') {
      console.log('Skipping deliveries fetch for non-active/paused subscription:', currentSubscriptionId);
      return;
    }
    
    // Set fetching flag before the async operation
    fetchingDeliveriesId.current = currentSubscriptionId;
    console.log('Starting to fetch deliveries for subscription:', currentSubscriptionId);
    
    const fetchDeliveries = async () => {
      try {
        // Fetch upcoming deliveries
        const upcomingData = await deliveryService.getSubscriptionDeliveries(currentSubscriptionId);
        // Only update if we're still on the same subscription
        if (fetchingDeliveriesId.current === currentSubscriptionId) {
          const upcomingWithId = (upcomingData as any[]).map((d) => ({
            ...d,
            id: 'id' in d && typeof d.id === 'string' ? d.id : undefined,
            subscriptionId: currentSubscriptionId,
            timeSlot: d.timeSlot || '9:00 AM - 12:00 PM'
          } as EnhancedDeliverySchedule));
          
          setUpcomingDeliveries(upcomingWithId);
          console.log(`Fetched ${upcomingWithId.length} upcoming deliveries for:`, currentSubscriptionId);
          
          // Fetch past deliveries using a subscription-specific endpoint
          try {
            const pastData = await deliveryService.getPastDeliveries(currentSubscriptionId);
            // Only update if we're still on the same subscription
            if (fetchingDeliveriesId.current === currentSubscriptionId) {
              const pastWithId = pastData.map((d: any) => ({
                ...d,
                id: d.id,
                subscriptionId: d.subscriptionId || currentSubscriptionId
              }));
              
              setPastDeliveries(pastWithId);
              console.log(`Fetched ${pastWithId.length} past deliveries for:`, currentSubscriptionId);
              
              // Mark this subscription's deliveries as loaded
              loadedDeliveriesIds.current.add(currentSubscriptionId);
            }
          } catch (error) {
            console.error(`Error fetching past deliveries for ${currentSubscriptionId}:`, error);
            // Don't show an error toast for past deliveries
          }
        }
      } catch (error: any) {
        console.error(`Error fetching upcoming deliveries for ${currentSubscriptionId}:`, error);
        
        // Handle specific 404 error for subscription not found
        if (error.response?.status === 404 && (error.response?.data?.error === "Subscription not found")) {
          console.warn(`Subscription ${currentSubscriptionId} no longer exists in the database, but is still in local state`);
          
          // Remove this subscription ID from loaded lists to prevent future requests
          loadedDeliveriesIds.current.delete(currentSubscriptionId);
          loadedSubscriptionIds.current.delete(currentSubscriptionId);
          
          // Redirect to subscription list
          toast.error('The subscription is no longer available');
          navigate('/subscriptions', { replace: true });
          
          // Do not show error toast for deleted subscriptions
          return;
        }
        
        // Only show an error toast for other types of errors
        toast.error('Failed to load delivery information');
      } finally {
        // Only reset if we're still fetching for this subscription
        if (fetchingDeliveriesId.current === currentSubscriptionId) {
          fetchingDeliveriesId.current = null;
        }
      }
    };
    
    fetchDeliveries();
  }, [activeSubscriptionIndex, loading, subscriptions, subscriptionId, navigate]);

  // Filter subscriptions
  const { active: activeSubscriptions, cancelled: cancelledSubscriptions } = filterSubscriptions(subscriptions);
  
  const hasActiveSubscription = activeSubscriptions.length > 0 && !loading;
  const noActiveSubscriptionConfirmed = !loading && activeSubscriptions.length === 0;

  console.log('Current active subscription:', {
    index: activeSubscriptionIndex,
    subscription: subscriptions[activeSubscriptionIndex],
    deliverySettings: subscriptions[activeSubscriptionIndex]?.deliverySettings,
    day_of_week: subscriptions[activeSubscriptionIndex]?.deliverySettings?.day_of_week,
    time_slots: subscriptions[activeSubscriptionIndex]?.deliverySettings?.time_slots
  });

  const handleSkipFromUI = () => {
    if (activeSubscription.status === 'active' && activeSubscription.nextDeliveryDate) {
      setSkipDate(format(new Date(activeSubscription.nextDeliveryDate), 'yyyy-MM-dd'));
      setShowSkipModal(true);
    } else {
      toast.error('No upcoming delivery available');
    }
  };

  
  console.log('Active subscription delivery info:', {
    id: activeSubscription.id,
    status: activeSubscription.status,
    nextDeliveryDate: activeSubscription.nextDeliveryDate,
  });

  const handleSubscriptionSelect = (index: number) => {
    if (index === activeSubscriptionIndex || !subscriptions[index]?.id) return;
    
    console.log('User manually selected subscription at index:', index, {
      current: activeSubscriptionIndex,
      status: subscriptions[index]?.status,
      wasUserSelected: userHasSelectedSubscription.current,
      initialSelectionDone: hasInitialActiveSelectionDone.current
    });
    
    userHasSelectedSubscription.current = true;
    hasInitialActiveSelectionDone.current = true;
    
    
    isSwitchingSubscription.current = false;
    loadingSubscriptionId.current = null;
    
    
    if (subscriptions[index]?.id) {
      loadedSubscriptionIds.current.add(subscriptions[index].id as string);
      
      // Force refresh of delivery data for the newly selected subscription
      if (loadedDeliveriesIds.current.has(subscriptions[index].id as string)) {
        console.log('Clearing cached delivery data for newly selected subscription');
        loadedDeliveriesIds.current.delete(subscriptions[index].id as string);
      }
    }
    
    
    navigate(`/subscriptions/${subscriptions[index].id}`);
  };
  
  console.log('Current selection state:', {
    activeIndex: activeSubscriptionIndex,
    hasUserSelected: userHasSelectedSubscription.current,
    initialSelectionDone: hasInitialActiveSelectionDone.current,
    loadedIds: loadedSubscriptionIds.current.size,
    loading: loading
  });

  // Get active subscription data
  const getActiveSubscriptionData = () => {
    if (subscriptions.length === 0) {
      return (
        <div className="text-center py-8">
          <p className="text-gray-600">No subscriptions found.</p>
        </div>
      );
    }

    const subscriptionToShow = subscriptions[activeSubscriptionIndex];
    
    // Show empty state if no valid subscription
    if (!subscriptionToShow) {
      return (
        <div className="text-center py-8">
          <p className="text-gray-600">No valid subscription selected.</p>
        </div>
      );
    }

    // Show loading indicator only when switching subscriptions, loading that specific ID
    // and it's not a manual user selection
    const isLoading = isSwitchingSubscription.current && 
                     loadingSubscriptionId.current === subscriptionToShow.id && 
                     !userHasSelectedSubscription.current;
                     
    if (isLoading) {
      return (
        <div className="text-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 mx-auto"></div>
          <p className="mt-2 text-sm text-gray-600">{t('subscription.subscriptionDetails.subscriptionLoading')}</p>
        </div>
      );
    }

    // Determine the effective status
    const getEffectiveStatus = (sub: any) => {
      // Handle both field name formats for compatibility
      const remainingCount = sub.remainingDeliveries || sub.remaining_deliveries;
      
      if (sub.status === 'active' && 
          sub.isRecurring === false && 
          remainingCount === 0) {
        return 'pending_renewal';
      }
      return sub.status;
    };

    const effectiveStatus = getEffectiveStatus(subscriptionToShow);

    // Filter deliveries based on subscription status - pending_renewal and past_due should be treated like active for delivery display
    const filteredUpcomingDeliveries = (effectiveStatus === 'active' || effectiveStatus === 'pending_renewal' || effectiveStatus === 'past_due') 
      ? upcomingDeliveries.filter(d => d.subscriptionId === subscriptionToShow.id)
      : [];

    // Display subscription content
    return (
      <SubscriptionMainContent 
        activeSubscription={subscriptionToShow as unknown as SubscriptionState}
        upcomingDeliveries={filteredUpcomingDeliveries}
        pastDeliveries={pastDeliveries.filter(d => d.subscriptionId === subscriptionToShow.id)}
        activeTab={activeTab}
        setActiveTab={setActiveTab}
        processingDeliveryId={processingDeliveryId}
        loading={false}
        onSkip={handleSkipFromUI}
        onPause={handlePauseSubscription}
        onResume={handleResumeSubscription}
        onCancel={() => handleCancelSubscription(navigate)}
        onUpgrade={async () => {
          if (activeSubscription?.id) {
            try {
              await upgradeSubscription(activeSubscription.id);
              toast.success('Subscription upgraded successfully!');
              // No need to refresh data as the user will be redirected or data is already updated
            } catch (error: any) {
              console.error("Error initiating subscription upgrade:", error);
              
              // Check for specific error message about no remaining deliveries
              const errorMessage = error.response?.data?.message || error.message || '';
              const errorCode = error.response?.data?.code || '';
              
              if (errorMessage.includes('No remaining deliveries to upgrade') || 
                  errorCode === 'VALIDATION_ERROR' && errorMessage.includes('No remaining deliveries')) {
                // Show a specific error message with option to start new subscription
                toast.error(
                  t('subscription.renewal.errorMessages.noRemainingDeliveriesToUpgrade'),
                  {
                    duration: 6000,
                    style: {
                      maxWidth: '400px',
                    },
                  }
                );
                
                // Show a follow-up notification with action
                setTimeout(() => {
                  toast((toastInstance) => (
                    <div className="flex flex-col space-y-2">
                      <div className="font-medium text-gray-900">
                        {t('subscription.renewal.errorMessages.upgradeNotAvailable')}
                      </div>
                      <button
                        onClick={() => {
                          toast.dismiss(toastInstance.id);
                          navigate('/subscription');
                        }}
                        className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors text-sm"
                      >
                        {t('subscription.renewal.errorMessages.startNewSubscription')}
                      </button>
                    </div>
                  ), {
                    duration: 8000,
                    style: {
                      maxWidth: '400px',
                    },
                  });
                }, 1000);
              } else {
                // Show generic error message for other errors
                toast.error("Failed to initiate upgrade. Please try again.");
              }
            }
          }
        }}
        onRenew={async () => {
          // Check if subscription purchases are enabled
          if (!FEATURES.ENABLE_SUBSCRIPTION_PURCHASES) {
            toast.error(t('common.maintenance.title'));
            return;
          }

          if (activeSubscription?.id) {
            try {
              // No need to show success toast here - the user will be redirected to payment
              await renewSubscription(activeSubscription.id);
              // No need to refresh data as the user will be redirected
            } catch (error) {
              console.error("Error initiating subscription renewal:", error);
              toast.error("Failed to initiate renewal. Please try again.");
            }
          }
        }}
        onSkipDeliveryFromHistory={handleSkipDeliveryFromHistory}
        onAddressUpdate={handleAddressSelected}
        hideTitle={true}
      />
    );
  };

  // Handle address selection from the modal
  const handleAddressSelected = (selectedAddress: Address) => {
    console.log('Address selected in SubscriptionManagement:', selectedAddress);
    
    if (activeSubscription?.id) {
      // Update local state first
      updateAddress(selectedAddress);
      
      // Update address via API - no toast notifications here, let the hook handle it
      console.log('Calling handleAddressUpdate with selected address');
      handleAddressUpdate(selectedAddress)
        .then(() => {
          // Only fetch fresh data after update
          console.log(`SubscriptionManagement: Fetching fresh data for subscription ${activeSubscription.id}`);
          fetchSubscriptionData(activeSubscription.id as string, true);
        })
        .catch(error => {
          console.error('Error updating address:', error);
        });
    } else {
      console.error('No active subscription found when trying to update address');
    }
    
    // Close the modal
    console.log('Closing address modal from handleAddressSelected');
    setShowAddressModal(false);
  };

  // When handling address update, now show the modal instead of navigating
  const handleUpdateAddress = () => {
    console.log('Showing address modal from handleUpdateAddress');
    setShowAddressModal(true);
  };

  // Safe handler for closing the address modal
  const handleCloseAddressModal = () => {
    console.log('Closing address modal from SubscriptionManagement');
    setShowAddressModal(false);
  };

  // Redirect to subscription list if no ID is provided
  if (!subscriptionId) {
    useEffect(() => {
      navigate('/subscriptions', { replace: true });
    }, [navigate]);
    
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900 mx-auto"></div>
          <p className="mt-4 text-gray-600">Redirecting to subscription list...</p>
        </div>
      </div>
    );
  }

  // Render loading state only for initial load
  if (loading && subscriptions.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900 mx-auto"></div>
          <p className="mt-4 text-gray-600">{t('subscription.subscriptionDetails.subscriptionLoading')}</p>
        </div>
      </div>
    );
  }
  
  if (noActiveSubscriptionConfirmed) {
    return (
      <div className="min-h-screen bg-gray-50 p-4">
        {/* Shutdown Notice */}
        <ShutdownNotice />
        
        <NoSubscriptionView 
          cancelledSubscriptions={cancelledSubscriptions}
          onToggleCancelled={() => setShowCancelledSubscriptions(!showCancelledSubscriptions)}
          onSelectCancelled={setSelectedCancelledSubscription}
          onStartSubscription={() => navigate('/subscription')}
          hasExpiredSubscriptions={cancelledSubscriptions.length > 0}
          displayExpired={showCancelledSubscriptions}
        />

        <SubscriptionModals
          showSkipModal={showSkipModal}
          setShowSkipModal={setShowSkipModal}
          handleSkipDelivery={handleSkipDelivery}
          nextDeliveryDate={activeSubscription.nextDeliveryDate}
          loading={loading}
          selectedCancelledSubscription={selectedCancelledSubscription}
          setSelectedCancelledSubscription={setSelectedCancelledSubscription}
        />
      </div>
    );
  }

  return (
    <main className="min-h-screen bg-gray-50">
      <PageHeader title={t('subscription.subscriptionDetails.title')} backPath="/subscriptions" />

      {/* Shutdown Notice */}
      <ShutdownNotice />

      <SubscriptionHeader
        subscriptions={subscriptions as unknown as SubscriptionState[]}
        activeSubscriptions={activeSubscriptions}
        cancelledSubscriptions={cancelledSubscriptions}
        activeSubscriptionIndex={activeSubscriptionIndex}
        showCancelledSubscriptions={showCancelledSubscriptions}
        setShowCancelledSubscriptions={setShowCancelledSubscriptions}
        setActiveSubscriptionIndex={handleSubscriptionSelect}
        hideTitle={true}
      />

      {getActiveSubscriptionData()}

      <SubscriptionModals
        showSkipModal={showSkipModal}
        setShowSkipModal={setShowSkipModal}
        handleSkipDelivery={handleSkipDelivery}
        nextDeliveryDate={activeSubscription.nextDeliveryDate}
        loading={loading}
        selectedCancelledSubscription={selectedCancelledSubscription}
        setSelectedCancelledSubscription={setSelectedCancelledSubscription}
      />
      
      {/* Address Management Modal */}
      {showAddressModal && (
        <AddressManagementModal
          isOpen={showAddressModal}
          onClose={handleCloseAddressModal}
          onAddressSelect={handleAddressSelected}
          selectedAddress={activeSubscription.address ? 
            { ...activeSubscription.address, id: 'temp-id' } as AddressWithId : 
            null}
          isSubscription={true}
        />
      )}
    </main>
  );
} 